import { Section } from '@/features/projects/types';
import { EditableCtaWrapper } from '../EditableCtaWrapper';
import { ButtonStyleId, BUTTON_STYLES, getButtonStyles, DEFAULT_BUTTON_STYLE } from '@/constants/layoutTypes';

interface FinalCTASectionProps {
    content: {
        headline: string;
        subheadline: string;
        buttonText: string;
        buttonLink?: string;
        buttonAction?: 'scroll' | 'url';
    };
    primaryColor: string;
    isEditing: boolean;
    onEdit: (section: string, field: string, value: string) => void;
    sizeClasses: { heading: string; subheading: string; body: string; small: string };
    sections?: Section[];
    buttonStyle?: ButtonStyleId;
    onButtonStyleChange?: (style: ButtonStyleId) => void;
    onCtaClick?: (source: 'finalCta') => void;
}

export function FinalCTASection({
    content,
    primaryColor,
    isEditing,
    onEdit,
    sizeClasses,
    sections = [],
    buttonStyle = DEFAULT_BUTTON_STYLE,
    onButtonStyleChange,
    onCtaClick
}: FinalCTASectionProps) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-4' : '';
    const styleOption = BUTTON_STYLES.find(s => s.id === buttonStyle) || BUTTON_STYLES[0];
    const inlineStyles = getButtonStyles(buttonStyle, primaryColor);

    return (
        <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white @container">
            <div className="max-w-4xl mx-auto text-center">
                <h2
                    className={`${sizeClasses.heading} font-bold text-gray-900 mb-6 ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('finalCta', 'headline', e.currentTarget.textContent || '')}
                >
                    {content.headline}
                </h2>
                <p
                    className={`${sizeClasses.subheading} text-gray-600 mb-10 max-w-2xl mx-auto ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('finalCta', 'subheadline', e.currentTarget.textContent || '')}
                >
                    {content.subheadline}
                </p>

                <EditableCtaWrapper
                    text={content.buttonText}
                    link={content.buttonLink}
                    action={content.buttonAction}
                    buttonStyle={buttonStyle}
                    primaryColor={primaryColor}
                    isEditing={isEditing}
                    sections={sections}
                    onUpdate={(text, link, action) => {
                        onEdit('finalCta', 'buttonText', text);
                        onEdit('finalCta', 'buttonLink', link);
                        onEdit('finalCta', 'buttonAction', action);
                    }}
                    onStyleChange={onButtonStyleChange}
                >
                    <button
                        className={`${styleOption.className} text-lg`}
                        style={inlineStyles}
                        onClick={() => {
                            onCtaClick?.('finalCta');
                            if (content.buttonAction === 'url' && content.buttonLink) {
                                window.open(content.buttonLink, '_blank');
                            } else if (content.buttonLink) {
                                document.getElementById(content.buttonLink)?.scrollIntoView({ behavior: 'smooth' });
                            }
                        }}
                    >
                        {content.buttonText}
                    </button>
                </EditableCtaWrapper>
            </div>
        </section>
    );
}
